import { useEffect, useState } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { isAppKey, type AppKey } from '@qms360/shared';
import { AppTopBar, Breadcrumbs } from '../../components/AppTopBar.js';
import { PageMessage } from '../AppHome.js';
import { ApiError } from '../../lib/api.js';
import {
  fetchJobs,
  fetchMapping,
  fetchReconciliation,
  fetchSyncErrors,
  ignoreSyncError,
  retrySyncError,
  runProjectSync,
  saveMapping,
  type SyncError,
  type SyncJob,
} from '../../lib/data-sync-api.js';
import { t, formatDateTime } from '../../lib/i18n.js';

/**
 * The Data Sync Cockpit (spec §13.4).
 *
 * The platform sync's settings live in the Integration Cockpit with the other
 * connections; this is where it is RUN and WATCHED: what the last run found, the
 * records it could not use, the field mapping, and every job that runs by itself.
 */

const JOB_LABELS: Record<string, string> = {
  project_sync: 'Platform project sync',
  escalation_sweep: 'Escalation sweep',
  deadline_sweep: 'Missing-submission check',
  connector_health: 'Connection health check',
  email_delivery: 'Email delivery',
  push_delivery: 'Push delivery',
  distribution: 'Scheduled report',
  'distribution:memo': 'Approval memo',
};

const OUTCOME_LABELS: Record<string, string> = {
  running: 'Running',
  succeeded: 'Succeeded',
  partial: 'Partly succeeded',
  failed: 'Failed',
  queued: 'Queued',
};

const TRIGGER_LABELS: Record<string, string> = { schedule: 'Scheduled', manual: 'By hand', retry: 'Retry' };

function when(iso: string | null): string {
  return iso ? formatDateTime(iso, { dateStyle: 'medium', timeStyle: 'short' }) : '—';
}

export function DataSync() {
  const params = useParams<{ app: string }>();
  const app = params.app;
  const reconciliation = useQuery({ queryKey: ['sync', 'reconciliation'], queryFn: fetchReconciliation, retry: false });

  if (!app || !isAppKey(app)) {
    return <PageMessage title={t("Unknown application")} body="That application does not exist." />;
  }

  return (
    <div className="min-h-full">
      <AppTopBar app={app as AppKey} />
      <Breadcrumbs app={app as AppKey} trail={['Settings', 'Data Sync Cockpit']} />

      <main className="mx-auto max-w-6xl px-4 pb-16 pt-4">
        <h1 className="m-0 text-2xl font-semibold">{t("Data Sync Cockpit")}</h1>
        <p className="mt-1 text-sm text-content-secondary">
          {t("The platform project sync, and every job that runs by itself. The sync's connection is set up in the")}{' '}
          <Link to={`/${app}/settings/integrations`} className="text-ag-purple">
            {t("Integration Cockpit")}
          </Link>
          .
        </p>

        {reconciliation.isLoading && <p className="mt-6 text-content-secondary">{t("Loading…")}</p>}
        {reconciliation.error && (
          <section className="mt-6 rounded-md border border-border bg-tone-info-surface p-6" role="status">
            <h2 className="m-0 text-base font-semibold">{t("Not available to your role")}</h2>
            <p className="mb-0 mt-2 text-sm text-content-secondary">
              {t("The Data Sync Cockpit needs integration authority. Ask an administrator if you need it.")}
            </p>
          </section>
        )}

        {reconciliation.data && (
          <>
            <SyncSummary />
            <ErrorQueue />
            <MappingStudio />
            <JobMonitor />
          </>
        )}
      </main>
    </div>
  );
}

function SyncSummary() {
  const queryClient = useQueryClient();
  const reconciliation = useQuery({ queryKey: ['sync', 'reconciliation'], queryFn: fetchReconciliation });
  const jobs = useQuery({ queryKey: ['sync', 'can-run'], queryFn: () => fetchJobs({ jobKey: 'project_sync' }) });
  const [error, setError] = useState<string | null>(null);

  const run = useMutation({
    mutationFn: runProjectSync,
    onSuccess: () => {
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['sync'] });
    },
    onError: (caught) => setError(caught instanceof ApiError ? caught.message : t("The sync could not be run.")),
  });

  const last = reconciliation.data?.lastRun;
  const summary = last?.summary ?? {};

  return (
    <section className="mt-6 rounded-md border border-border p-5" aria-labelledby="summary-heading">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h2 id="summary-heading" className="m-0 text-lg font-semibold">
          {t("Platform project sync")}
        </h2>
        {jobs.data?.canRun && (
          <div className="flex gap-2">
            <button
              type="button"
              disabled={run.isPending}
              onClick={() => run.mutate(true)}
              className="rounded border border-border px-3 py-1.5 text-sm disabled:opacity-60"
            >
              {t("Dry run")}
            </button>
            <button
              type="button"
              disabled={run.isPending}
              onClick={() => run.mutate(false)}
              className="rounded bg-ag-purple px-3 py-1.5 text-sm text-white disabled:opacity-60"
            >
              {run.isPending ? t("Running…") : t("Run now")}
            </button>
          </div>
        )}
      </div>
      <p className="mb-0 mt-1 text-xs text-content-secondary">
        {t("A dry run reads the feed and reports what it would change, and changes nothing. With sandbox on, every run is a dry run.")}
      </p>

      {error && (
        <p role="alert" className="mt-3 text-sm" style={{ color: 'var(--tone-critical)' }}>
          {error}
        </p>
      )}

      {!last ? (
        <p className="mb-0 mt-3 text-sm text-content-secondary">{t("It has not run yet.")}</p>
      ) : (
        <div className="mt-3 text-sm" data-testid="last-run">
          <p className="m-0">
            {t("Last run")}{' '}{when(last.startedAt)} · {TRIGGER_LABELS[last.trigger] ?? last.trigger} ·{' '}
            <strong>{OUTCOME_LABELS[last.outcome] ?? last.outcome}</strong>
            {last.dryRun ? t(" · dry run, nothing changed") : ''}
            {last.fieldMappingVersion !== null ? t(" · mapping v{0}", { 0: last.fieldMappingVersion }) : ''}
          </p>
          {summary.error ? (
            <p className="mb-0 mt-2" style={{ color: 'var(--tone-critical)' }}>
              {summary.error}
            </p>
          ) : (
            <p className="mb-0 mt-2">
              {last.recordsRead}{' '}{t("in the feed:")}{' '}{summary.created ?? 0} {last.dryRun ? t("would be created") : t("created")},{' '}
              {summary.updated ?? 0} {last.dryRun ? t("would be updated") : t("updated")}, {summary.unchanged ?? 0}{' '}{t("unchanged,")}{' '}
              {summary.failed ?? 0}{' '}{t("could not be used.")}
            </p>
          )}
          {(summary.missingAtSource?.length ?? 0) > 0 && (
            <p className="mb-0 mt-2" data-testid="missing-at-source">
              {t("No longer in the feed, kept active until someone decides:")}{' '}
              {summary.missingAtSource!.map((project) => project.code).join(', ')}.
            </p>
          )}
          <p className="mb-0 mt-2 text-content-secondary">
            {reconciliation.data?.localOnly ?? 0}{' '}{t("project(s) were made in QMS360 and are not linked to the platform.")}
          </p>
        </div>
      )}
    </section>
  );
}

function ErrorQueue() {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<'open' | 'fixed' | 'ignored'>('open');
  const [reasons, setReasons] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const errors = useQuery({ queryKey: ['sync', 'errors', status], queryFn: () => fetchSyncErrors(status) });

  const done = () => {
    setError(null);
    void queryClient.invalidateQueries({ queryKey: ['sync'] });
  };
  const fail = (caught: unknown) => setError(caught instanceof ApiError ? caught.message : t("That could not be done."));
  const retry = useMutation({ mutationFn: retrySyncError, onSuccess: done, onError: fail });
  const ignore = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => ignoreSyncError(id, reason),
    onSuccess: done,
    onError: fail,
  });

  return (
    <section className="mt-8" aria-labelledby="errors-heading">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h2 id="errors-heading" className="m-0 text-lg font-semibold">
          {t("Records the sync could not use")}
        </h2>
        <select
          value={status}
          onChange={(event) => setStatus(event.target.value as typeof status)}
          className="rounded border border-border px-2 py-1 text-sm"
          aria-label={t("Which records")}
        >
          <option value="open">{t("Waiting for a decision")}</option>
          <option value="fixed">{t("Applied since")}</option>
          <option value="ignored">{t("Set aside")}</option>
        </select>
      </div>
      {error && (
        <p role="alert" className="mt-2 text-sm" style={{ color: 'var(--tone-critical)' }}>
          {error}
        </p>
      )}
      {errors.data && errors.data.errors.length === 0 && (
        <p className="mt-3 text-sm text-content-secondary">{t("None.")}</p>
      )}
      <ul className="m-0 mt-3 list-none space-y-2 p-0">
        {errors.data?.errors.map((entry: SyncError) => (
          <li key={entry.id} className="rounded border border-border p-3 text-sm" data-testid="sync-error">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <strong>{entry.recordKey ?? t("A record with no identifier")}</strong>
              <span className="text-xs text-content-secondary">
                {t("tried")}{' '}{entry.attempts}{' '}{t("time")}{entry.attempts === 1 ? '' : 's'} · {when(entry.updatedAt)}
              </span>
            </div>
            <p className="mb-0 mt-1">{entry.error}</p>
            <details className="mt-1">
              <summary className="cursor-pointer text-xs text-content-secondary">{t("The record as the platform sent it")}</summary>
              <pre className="mt-1 overflow-x-auto rounded bg-surface p-2 text-xs">{JSON.stringify(entry.record, null, 2)}</pre>
            </details>
            {entry.resolutionNote && <p className="mb-0 mt-1 text-xs text-content-secondary">{entry.resolutionNote}</p>}
            {entry.status === 'open' && errors.data?.canResolve && (
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  disabled={retry.isPending}
                  onClick={() => retry.mutate(entry.id)}
                  className="rounded border border-border px-2 py-1 text-xs disabled:opacity-60"
                >
                  {t("Try again")}
                </button>
                <label className="sr-only" htmlFor={`reason-${entry.id}`}>
                  {t("Why set it aside")}
                </label>
                <input
                  id={`reason-${entry.id}`}
                  type="text"
                  placeholder={t("Why set it aside")}
                  value={reasons[entry.id] ?? ''}
                  onChange={(event) => setReasons({ ...reasons, [entry.id]: event.target.value })}
                  className="min-w-[16rem] rounded border border-border px-2 py-1 text-xs"
                />
                <button
                  type="button"
                  disabled={ignore.isPending || (reasons[entry.id] ?? '').trim().length < 5}
                  onClick={() => ignore.mutate({ id: entry.id, reason: reasons[entry.id]!.trim() })}
                  className="rounded border border-border px-2 py-1 text-xs disabled:opacity-60"
                >
                  {t("Set aside")}
                </button>
              </div>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

function MappingStudio() {
  const queryClient = useQueryClient();
  const mapping = useQuery({ queryKey: ['sync', 'mapping'], queryFn: fetchMapping });
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (mapping.data) setDraft(mapping.data.current.mapping);
  }, [mapping.data]);

  const save = useMutation({
    mutationFn: () => saveMapping(draft, note.trim() || null),
    onSuccess: (data) => {
      setError(null);
      setNote('');
      queryClient.setQueryData(['sync', 'mapping'], data);
    },
    onError: (caught) => setError(caught instanceof ApiError ? caught.message : t("The mapping could not be saved.")),
  });

  if (!mapping.data) return null;
  const { current, fields, canChange, versions } = mapping.data;

  return (
    <section className="mt-8 rounded-md border border-border p-5" aria-labelledby="mapping-heading">
      <h2 id="mapping-heading" className="m-0 text-lg font-semibold">
        {t("Field mapping")}
      </h2>
      <p className="mb-0 mt-1 text-sm text-content-secondary">
        {t("Which field in the platform's feed fills each QMS360 field, as a name or a dot path (for example")}{' '}
        <code>{t("attributes.projectCode")}</code>).{' '}
        {current.version === 0 ? t("Using the default mapping.") : t("Version {0}.", { 0: current.version })}{' '}{t("Saving adds a new version; every run records the version it used.")}
      </p>
      <div className="mt-3 grid gap-2 sm:grid-cols-2">
        {fields.map((field) => (
          <label key={field.key} className="flex flex-col gap-1 text-sm">
            <span className="text-content-secondary">
              {t(field.label)}
              {field.required ? t(" (required)") : ''}
            </span>
            <input
              type="text"
              value={draft[field.key] ?? ''}
              disabled={!canChange}
              onChange={(event) => setDraft({ ...draft, [field.key]: event.target.value })}
              className="rounded border border-border px-2 py-1 font-mono text-xs"
            />
          </label>
        ))}
      </div>
      {canChange && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <input
            type="text"
            aria-label={t("Note for this version (optional)")}
            placeholder={t("Note for this version (optional)")}
            value={note}
            onChange={(event) => setNote(event.target.value)}
            className="min-w-[18rem] rounded border border-border px-2 py-1 text-sm"
          />
          <button
            type="button"
            disabled={save.isPending}
            onClick={() => save.mutate()}
            className="rounded bg-ag-purple px-3 py-1.5 text-sm text-white disabled:opacity-60"
          >
            {t("Save as a new version")}
          </button>
        </div>
      )}
      {error && (
        <p role="alert" className="mb-0 mt-2 text-sm" style={{ color: 'var(--tone-critical)' }}>
          {error}
        </p>
      )}
      {versions.length > 0 && (
        <details className="mt-3 text-sm">
          <summary className="cursor-pointer text-content-secondary">{t("Earlier versions (")}{versions.length})</summary>
          <ul className="m-0 mt-2 list-none space-y-1 p-0 text-xs">
            {versions.map((entry) => (
              <li key={entry.version}>
                v{entry.version} · {when(entry.createdAt)}
                {entry.note ? ` · ${entry.note}` : ''}
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}

function JobMonitor() {
  const jobs = useInfiniteQuery({
    queryKey: ['sync', 'jobs'],
    queryFn: ({ pageParam }) => fetchJobs({ before: pageParam }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
  });
  const rows: SyncJob[] = jobs.data?.pages.flatMap((page) => page.jobs) ?? [];

  return (
    <section className="mt-10" aria-labelledby="jobs-heading">
      <h2 id="jobs-heading" className="m-0 text-lg font-semibold">
        {t("Job monitor")}
      </h2>
      <p className="mb-0 mt-1 text-sm text-content-secondary">
        {t("Every run of everything that runs by itself. Delivery appears only when there was something to send.")}
      </p>
      {rows.length === 0 && !jobs.isLoading && <p className="mt-3 text-sm text-content-secondary">{t("Nothing has run yet.")}</p>}
      {rows.length > 0 && (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="text-content-secondary">
                <th className="border-b border-border p-2 text-start font-normal">{t("Job")}</th>
                <th className="border-b border-border p-2 text-start font-normal">{t("Started")}</th>
                <th className="border-b border-border p-2 text-start font-normal">{t("How")}</th>
                <th className="border-b border-border p-2 text-start font-normal">{t("Outcome")}</th>
                <th className="border-b border-border p-2 text-end font-normal">{t("Read")}</th>
                <th className="border-b border-border p-2 text-end font-normal">{t("Written")}</th>
                <th className="border-b border-border p-2 text-end font-normal">{t("Failed")}</th>
                <th className="border-b border-border p-2 text-end font-normal">{t("Took")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((job) => (
                <tr key={job.id} data-testid="job-row">
                  <td className="border-b border-border p-2">
                    {JOB_LABELS[job.jobKey] ?? job.jobKey}
                    {job.dryRun ? <span className="text-xs text-content-secondary">{' '}{t("· dry run")}</span> : null}
                  </td>
                  <td className="border-b border-border p-2">{when(job.startedAt)}</td>
                  <td className="border-b border-border p-2">{TRIGGER_LABELS[job.trigger] ?? job.trigger}</td>
                  <td className="border-b border-border p-2">
                    {OUTCOME_LABELS[job.outcome] ?? job.outcome}
                    {job.summary.error && <div className="text-xs text-content-secondary">{job.summary.error}</div>}
                  </td>
                  <td className="border-b border-border p-2 text-end">{job.recordsRead}</td>
                  <td className="border-b border-border p-2 text-end">{job.recordsWritten}</td>
                  <td className="border-b border-border p-2 text-end">{job.recordsFailed}</td>
                  <td className="border-b border-border p-2 text-end">
                    {job.durationMs !== null ? `${(job.durationMs / 1000).toFixed(1)}s` : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {jobs.hasNextPage && (
        <button
          type="button"
          disabled={jobs.isFetchingNextPage}
          onClick={() => void jobs.fetchNextPage()}
          className="mt-3 rounded border border-border px-3 py-1.5 text-sm disabled:opacity-60"
        >
          {t("Older")}
        </button>
      )}
    </section>
  );
}
